import { videoAspectCss, type SessionVideoStatusView } from "@fiket/booth-schema";
import type { VideoStatus } from "../../store/router";

/**
 * 받기 화면의 영상 카드 판정 — 순수 함수 (BOOTH-60 지연 전달, vitest).
 * 키오스크는 Veo 완료를 기다리지 않으므로 받기 화면에 들어올 때 영상은 보통 `generating` 이다. 세션이 만들어지면
 * `GET /api/sessions/:code/video` 를 5초마다 폴링해 서버 쪽 상태(`remote`)로 카드를 바꾼다.
 */

export const SESSION_VIDEO_POLL_MS = 5_000;
/** 5초 × 60회 = 5분 — 받기 화면 무동작 120초보다 길어 사실상 화면이 살아 있는 동안 계속 본다. */
export const SESSION_VIDEO_POLL_MAX = 60;

export type VideoCardState = "none" | "pending" | "ready" | "failed";

/**
 * - 키오스크가 영상을 시작하지 않았으면(idle) 카드 없음
 * - 서버 응답이 오면 그 값이 우선: ready → 영상 카드, failed → 실패 안내, none(서버가 job 을 붙이지 못함) → 실패 안내
 * - 아직 서버 응답 전이면 키오스크 상태로: ready(완성 URL 보유) → 영상 카드, failed → 실패, generating → 제작 중
 */
export function deriveVideoCard(kiosk: VideoStatus, remote: SessionVideoStatusView | null): VideoCardState {
  if (kiosk === "idle") return "none";
  if (remote) {
    switch (remote.videoStatus) {
      case "ready":
        return remote.videoUrl ? "ready" : "pending";
      case "failed":
      case "none":
        return "failed";
      case "generating":
        return "pending";
    }
  }
  if (kiosk === "ready") return "ready";
  if (kiosk === "failed") return "failed";
  return "pending";
}

/** 폴링을 멈출 상태 — 제작 중이 아니면 끝. */
export function isVideoPollDone(remote: SessionVideoStatusView | null): boolean {
  return remote !== null && remote.videoStatus !== "generating";
}

/** QR 옆 안내 첫 줄 — 카드 상태별 문구 키. */
export function printShareBodyKey(card: VideoCardState): "desc1" | "bodyVideoPending" | "bodyWithVideo" | "bodyVideoFailed" {
  switch (card) {
    case "pending":
      return "bodyVideoPending";
    case "ready":
      return "bodyWithVideo";
    case "failed":
      return "bodyVideoFailed";
    default:
      return "desc1";
  }
}

/**
 * 영상 카드 프레임 비율(CSS `aspect-ratio`) — 서버 후처리 결과 `videoAspect`(BOOTH-85: 자막 있으면 2:3, 없으면 3:4 크롭) 를 따른다.
 * 응답 전·구 레코드·후처리 실패(`videoAspect` 없음) 는 3:4 — 9:16 원본의 검은 띠를 cover 로 가리는 기존 동작. 사진 카드는 인쇄 비율 그대로.
 */
export function videoFrameAspect(remote: SessionVideoStatusView | null): string {
  return videoAspectCss(remote?.videoAspect);
}
